import { Button, Input, Result, Form } from 'antd'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { contextCompanyIdSelector } from '../../../../../redux/context/company/selectors'
import { useIntegrationDocsURL } from '../../../../../utils/docs'
import DataUpdate from '../dataUpdate'
import { AppDispatch } from '../../../../../redux/store'
import { notificationAction } from '../../../../../redux/middleware/actions'
import { integrationClient } from '../../../../../services/api/integrationClient'

const Tripletex: React.FC = () => {
  const { t } = useTranslation()
  const integrationDocs = useIntegrationDocsURL()
  const companyId = useSelector(contextCompanyIdSelector)
  const dispatch: AppDispatch = useDispatch()

  const queryClient = useQueryClient()
  const [form] = Form.useForm()
  const [showForm, setShowForm] = React.useState(false)

  const { data: credentials } = useQuery({
    enabled: !showForm,
    queryKey: ['tripletex-authentication', companyId],
    queryFn: async () => {
      const response = await integrationClient.get(`/tripletex/authentication/${companyId}`)
      return response.data
    },
    retry: false
  })

  const setCredentialsMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await integrationClient.put(`/tripletex/authentication/${companyId}`, data)
      return response.data
    }
  })

  const authorized = !showForm && credentials?.httpCode === 200

  const submit = () => {
    form
      .validateFields()
      .then(data => {
        setCredentialsMutation.mutate(data, {
          onSuccess: response => {
            if (response?.httpCode !== 200) {
              dispatch(
                notificationAction({
                  type: 'error',
                  message: 'AUTHENTICATION_ERROR',
                  description: 'USERNAME_PASSWORD_MISMATCH'
                })
              )
              return
            }

            queryClient.invalidateQueries({ queryKey: ['tripletex-authentication', companyId] })
            setShowForm(false)
            dispatch(
              notificationAction({
                type: 'success',
                message: 'AUTHENTICATION_SUCCESS',
                description: 'IMPORT_SUCCESS_EMAIL'
              })
            )
          },
          onError: () => {
            dispatch(
              notificationAction({
                type: 'error',
                message: 'AUTHENTICATION_ERROR',
                description: 'USERNAME_PASSWORD_MISMATCH'
              })
            )
          }
        })
      })
      .catch(() => undefined)
  }

  const emptyCredentials = () => {
    form.resetFields()
    setCredentialsMutation.reset()
    setShowForm(true)
  }

  if (authorized) {
    return (
      <>
        <Result
          status="success"
          title={t('integrationsPage:authenticated')}
          extra={
            <>
              <Button onClick={emptyCredentials} type="primary">
                {t('integrationsPage:authenticate')}
              </Button>
              <DataUpdate page="tripletex" />
            </>
          }
        />
      </>
    )
  }
  return (
    <Form form={form} layout="vertical">
      <a href={integrationDocs.accounting('tripletex')} target="_blank" rel="noopener noreferrer">
        {t('header:manual')}
      </a>
      <Form.Item
        label="Employee Token"
        name="employeeToken"
        rules={[{ required: true, message: t('global:required-field') }]}
      >
        <Input.Password />
      </Form.Item>
      <Form.Item>
        <Button type="primary" onClick={() => submit()} loading={setCredentialsMutation.isPending}>
          {t('integrationsPage:authorize')}
        </Button>
      </Form.Item>
    </Form>
  )
}

export default Tripletex
